import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PopupContext } from 'components';
import { useContext } from 'react';
import { getPageParamFromLastPage } from 'utils/getPageParamFromLastPage';
import PRODUCTS_KEYS from '../products.keys';
import PRODUCTS_MUTATIONS from '../products.mutation';
import PRODUCTS_QUERIES from '../products.queries';

export const useFetchProductInventories = ({ page, pageSize, search, ordering, filters }) =>
  useQuery({
    queryKey: PRODUCTS_KEYS.PRODUCT_INVENTORIES_PAGINATED({
      page,
      pageSize,
      search,
      ordering,
      filters,
    }),
    queryFn: () =>
      PRODUCTS_QUERIES.FETCH_PRODUCT_INVENTORIES({
        page,
        pageSize,
        search,
        ordering,
        filters,
      }),
    staleTime: 1 * 60 * 1000, // 1 minute
  });

export const useFetchProductInventoriesInfinite = ({ pageSize, search, ordering, filters }) =>
  useInfiniteQuery({
    queryKey: PRODUCTS_KEYS.PRODUCT_INVENTORIES_INFINITE({ pageSize, filters, search, ordering }),
    queryFn: ({ pageParam = 1 }) =>
      PRODUCTS_QUERIES.FETCH_PRODUCT_INVENTORIES({
        page: pageParam,
        pageSize,
        search,
        ordering,
        filters,
      }),
    getNextPageParam: (lastPage) => getPageParamFromLastPage(lastPage),
    staleTime: 1 * 60 * 1000, // 1 minute
  });

export const useCreateInventory = () => {
  const queryClient = useQueryClient();
  const { closePopup } = useContext(PopupContext);
  return useMutation({
    mutationFn: PRODUCTS_MUTATIONS.CREATE_INVENTORY,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRODUCTS_KEYS.PRODUCT_INVENTORIES() });
      closePopup?.();
    },
  });
};

export const useUpdateInventory = () => {
  const queryClient = useQueryClient();
  const { closePopup } = useContext(PopupContext);
  return useMutation({
    mutationFn: PRODUCTS_MUTATIONS.UPDATE_INVENTORY,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRODUCTS_KEYS.PRODUCT_INVENTORIES() });
      closePopup?.();
    },
  });
};

export const useDeleteInventory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: PRODUCTS_MUTATIONS.DELETE_INVENTORY,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRODUCTS_KEYS.PRODUCT_INVENTORIES() });
    },
  });
};

export const useGenerateInventories = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: PRODUCTS_MUTATIONS.GENERATE_INVENTORIES,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRODUCTS_KEYS.PRODUCT_INVENTORIES() });
    },
  });
};
